import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button, IconCaretDown, Text } from '@vtex/admin-ui'
import { IconX } from '@vtex/shoreline'
import { DownloadItem } from '@/components/products-listing/components/DownloadCollapser/DownloadItem/DownloadItem'

import * as styles from './DownloadListCollapser.styles'

export interface DownloadItemProps {
  HashLink: string
  Type: string
  CreatedAt: string
  TotalRows: number
  Progress: number
  Status: string
  FileLink: string | null | undefined
}

interface DownloadListCollapserProps {
  items: Record<string, DownloadItemProps>
  className?: string
  position?: 'fixed' | 'inline'
  onClear: () => void
}

export const DownloadListCollapser = ({
  items,
  onClear,
}: DownloadListCollapserProps) => {
  const { formatMessage } = useIntl()
  const [isOpen, setIsOpen] = useState(true)
  const [isVisible, setIsVisible] = useState(true)
  const [itemsHistory, setItemsHistory] = useState<
    Record<string, DownloadItemProps>
  >({})

  const toggleOpen = () => setIsOpen(!isOpen)

  const itemsValues = useMemo(() => Object.values(itemsHistory), [itemsHistory])

  const downloadableItems = Object.values(itemsValues).filter(
    (item) =>
      item.Status !== 'Cancelled' &&
      item.Status !== 'CancellationRequested' &&
      item.FileLink !== ''
  )

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation()
    setItemsHistory({})
    setIsVisible(false)
    onClear()
  }

  useEffect(() => {
    if (Object.keys(items).length > 0) {
      setItemsHistory((prev) => ({
        ...prev,
        ...items,
      }))
      setIsVisible(true)
    }
  }, [items])

  const handleBeforeUnload = useCallback(() => {
    onClear()
  }, [onClear])

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [handleBeforeUnload])

  if (!isVisible || downloadableItems.length === 0) return null

  return (
    <div className={styles.container}>
      <div className={styles.collapserTitle} onClick={toggleOpen}>
        <div className="flex items-center gap-2">
          <Text variant="action1">
            {formatMessage({
              id: 'admin/admin.catalog.export-collapser.title',
              defaultMessage: 'Exports',
            })}
          </Text>
          {downloadableItems.length > 0 && (
            <Text variant="action2" tone="secondary">
              {downloadableItems.length}
            </Text>
          )}
        </div>
        <div className="flex items-center gap-2">
          {itemsValues.length >= 1 && (
            <div className={styles.icon}>
              <IconCaretDown data-status={isOpen ? 'open' : 'closed'} />
            </div>
          )}
          <Button
            icon={<IconX />}
            variant="neutralTertiary"
            aria-label={formatMessage({
              id: 'admin/admin.catalog.export-collapser.close-button',
              defaultMessage: 'Close',
            })}
            className={styles.icon}
            onClick={handleClose}
          />
        </div>
      </div>

      <div className={styles.content} data-open={isOpen}>
        {itemsValues
          .slice()
          .reverse()
          .map((item) => (
            <DownloadItem key={item.HashLink} item={item} />
          ))}
      </div>
    </div>
  )
}
export { DownloadItem }
